import {
  faFileInvoice,
  faPlus,
  faUser
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { clsx } from "clsx";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { type CollectionEventPayload } from "@kanpla/api-contract";

import { minorUnitToMajorUnit } from "@repo/system";
import { T } from "@repo/transifex";
import type { PaymentProvider } from "@repo/types";
import { useUserProductsPos } from "~/api/products/hooks/use-user-products";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { Button } from "~/components";
import { OrderCreateErrorDialog } from "~/components/OrderCreateErrorDialog";
import { useOpenCashDrawer } from "~/hooks";
import {
  useOrderCardReimbursementCreate,
  useOrderCreate,
  usePrintOrderReceipt
} from "~/hooks/queries/orders";
import { usePaymentCreate } from "~/hooks/queries/payments";
import { useAbortTransaction } from "~/hooks/queries/terminals";
import { logger } from "~/services/logger";
import { useOrderCardCreateV3 } from "~/hooks/queries/useOrderCardCreateV3.ts";
import { useSkipReceipt } from "~/hooks/use-skip-receipt";
import { useDeviceData } from "~/providers/app";
import {
  useEmployee,
  useIsEmployeeAuthenticated
} from "~/providers/store/employee";
import { calculateAmountForSaldo } from "~/utils/basket";
import { useSplitPaymentFlow } from "~/hooks/use-split-payment-flow";
import { autoId } from "~/utils/auto-id";
import { useBasketProductsOrder } from "~/providers/store/basket/hooks/use-basket-products-order";
import {
  useDiningOption,
  useShopperName
} from "~/providers/store/basket/hooks/use-basket";
import { hasAttendedSellConfig } from "~/utils/tills/has-attended-sell-config";
import { useUserBalance } from "~/hooks/use-user-balance";
import { useGetCollectOrder } from "~/api/collect/hooks/use-get-collect-order";
import { useOrderLinesToCollect } from "~/providers/store/collect/hooks";
import { parseOrderLinesToCollect } from "~/utils/collect/parse-order-lines-to-collect";
import {
  useCollectOrderLines,
  useCollectSuccessHandler
} from "~/api/collect/hooks/use-collect-order-lines";
import { collectActions } from "~/providers/store/collect/actions";
import { useVatLabel } from "~/hooks/use-vat-label";
import { resolveBasketTaxTotals } from "~/lib/basket-tax-totals";
import { useBasketContext } from "~/providers/store/basket/context";
import { useFreedomPayOrder } from "~/api/orders/hooks/use-create-order";
import { useOrderCardCreateV4 } from "~/hooks/queries/use-order-card-create-v4";
import { useIsIdentifyUserPending } from "~/hooks/use-identify-user";

import { toFreedomPayItems } from "./to-freedompay-items";
import { DiningOptionSwitch } from "./dining-option-switch";
import { CollectConfirmButton } from "./collect-confirm-button";
import { PaymentButtons } from "./payment-buttons";
import { PriceOverview } from "./price-overview";
import { Allowances } from "./allowances";

export const BasketFooter = () => {
  const { pathname } = useLocation();
  const [, setSearchParams] = useSearchParams();

  const { data: userProducts } = useUserProductsPos();

  const { os, osVersion } = useDeviceData();

  const diningOption = useDiningOption();
  const shopperName = useShopperName();

  const { groupName, employeeChildId, employeeCardHex } = useEmployee();
  const isUserAuthenticated = useIsEmployeeAuthenticated();

  const { vatLabel } = useVatLabel();

  const employeeUserId = userProducts?.user?.id ?? null;
  const balance = userProducts?.user?.balance ?? null;

  const { locale, currency } = useCurrentTillLocalisation();
  const { mutate: createOrder, status, error, reset } = useOrderCreate();
  const { mutate: abortTransaction } = useAbortTransaction();

  const {
    mutate: createCardOrderV3,
    status: cardStatusV3,
    reset: cardResetV3,
    error: cardErrorV3
  } = useOrderCardCreateV3({
    abortTransactionCallback: ({ orderId, serviceId }) => {
      abortTransaction({ orderId, serviceId });
    }
  });

  const { mutate: payOrder } = usePaymentCreate();

  const { mutate: startFreedompayPayment } = useFreedomPayOrder();

  const { mutateAsync: createOrderV4 } = useOrderCardCreateV4();

  const {
    mutate: createCardReimbursementOrder,
    status: cardReimbursementStatus,
    error: cardReimbursementError,
    reset: cardReimbursementReset
  } = useOrderCardReimbursementCreate({
    abortTransactionCallback: ({ orderId, serviceId }) => {
      abortTransaction({ orderId, serviceId });
    }
  });

  const openCashDrawer = useOpenCashDrawer();

  const { mutate: createReceipt } = usePrintOrderReceipt();

  const allowances = useBasketContext((state) => state.allowances);
  const productsInBasket = useBasketContext((state) => state.productsInBasket);
  const isBasketEmpty = useBasketContext((state) => state.isBasketEmpty);
  const unitPriceSubTotalInclVat = useBasketContext(
    (state) => state.sum.unitPriceSubTotalInclVat
  );
  const unitPriceTotalInclVat = useBasketContext(
    (state) => state.sum.unitPriceTotalInclVat
  );
  const unitPriceTotalExclVat = useBasketContext(
    (state) => state.sum.unitPriceTotalExclVat
  );
  const depositTotalInclVat = useBasketContext(
    (state) => state.sum.depositTotalInclVat
  );
  const depositTotalExclVat = useBasketContext(
    (state) => state.sum.depositTotalExclVat
  );
  const unitPriceOrderDiscountInclVat = useBasketContext(
    (state) => state.sum.orderDiscountTotalInclVat
  );

  const basketOrder = useBasketProductsOrder();

  const { data: till, status: tillStatus } = useCurrentTill();

  const skipReceipt = useSkipReceipt();

  const {
    isActive: isSplitPaymentActive,
    loading: isSplitPaymentLoading,
    orderId: splitPaymentOrderId,
    unitReceived: unitPriceSplitCashInclVat
  } = useSplitPaymentFlow();

  const isIdentifyUserPending = useIsIdentifyUserPending();

  const { data: collectOrder } = useGetCollectOrder({
    childId: employeeChildId ?? undefined
  });
  const orderLinesToCollect = useOrderLinesToCollect();

  const { mutate: collectOrderLines } = useCollectOrderLines();
  const handleCollectSuccess = useCollectSuccessHandler();

  const isCollectFlowActive = collectOrder && orderLinesToCollect.length > 0;

  const handleCreateOrderSuccess = ({ orderId }: { orderId: string }) => {
    if (!till) throw new Error("Till not found");

    if (
      till.type === "self-service" &&
      till.config.receiptPrintMode === "automatic"
    ) {
      createReceipt({ orderId, userId: employeeUserId });
    }

    skipReceipt(orderId);

    if (isCollectFlowActive) {
      const orderLines = parseOrderLinesToCollect({
        orderLineToCollectIds: orderLinesToCollect,
        activeOrderLines: collectOrder.activeOrderLines
      });

      const isFullyCollected =
        collectOrder.activeOrderLines.length === orderLinesToCollect.length;

      if (!employeeChildId) {
        throw new Error("Employee child id is required for collect flow");
      }

      collectOrderLines(
        {
          childId: employeeChildId,
          orderLines
        },
        {
          onSuccess: () => {
            if (hasAttendedSellConfig(till) && till.config.skipReceiptPage) {
              handleCollectSuccess({
                collectedOrderLines: orderLines.length,
                additionalProductsPurchased: basketOrder.length,
                isFullyCollected,
                forceRedirect: true
              });
            } else {
              collectActions.setSuccessInfo({
                collectedOrderLines: orderLines.length,
                additionalProductsPurchased: basketOrder.length,
                isFullyCollected
              });
            }
          }
        }
      );
    }
  };

  const handleCreateOrder = (paymentMethod: PaymentProvider) => {
    const info = diningOption
      ? {
          textInput: {
            [diningOption.inputId]: diningOption.value
          }
        }
      : undefined;

    if (paymentMethod === "card" || paymentMethod === "swish") {
      if (!os || !osVersion) throw new Error("OS or OS Version not provided");
      if (!till) throw new Error("Till not found");
      if (!till.sessionId) throw new Error("Till is not open");

      const orderId = autoId();

      if (unitPriceTotalInclVat < 0) {
        if (paymentMethod === "swish")
          throw new Error(
            "Swish payment method not supported for negative amount"
          );

        if (till.paymentGatewayConfig?.provider === "freedompay") {
          throw new Error(
            "FreedomPay card payments are not supported for negative amounts"
          );
        }

        createCardReimbursementOrder(
          {
            orderId,
            paymentMethod,
            basket: basketOrder,
            groupName: groupName || undefined,
            deviceInfo: { os, osVersion },
            info
          },
          {
            onSuccess: (_, { orderId }) => {
              createReceipt({ orderId, userId: employeeUserId });
            }
          }
        );

        return;
      }

      if (till.paymentGatewayConfig?.provider === "freedompay") {
        if (paymentMethod !== "card") {
          throw new Error(
            `FreedomPay card payments are not supported for ${paymentMethod} payment method`
          );
        }

        const paymentId = autoId();
        logger.debug("FreedomPay: sale MRC minted", {
          order_id: orderId,
          payment_id: paymentId,
          event: "freedompay_sale_mrc_minted"
        });
        const cardAmountUnitInclVat =
          unitPriceTotalInclVat - unitPriceSplitCashInclVat;

        startFreedompayPayment(
          {
            orderId,
            paymentId,
            totalPriceInclVat: minorUnitToMajorUnit(cardAmountUnitInclVat),
            taxTotal: minorUnitToMajorUnit(
              unitPriceTotalInclVat - unitPriceTotalExclVat
            ),
            items: toFreedomPayItems(basketOrder),
            createOrderAsync: () =>
              createOrderV4({
                tillId: till.id,
                orderId,
                basket: basketOrder,
                shopperName,
                paymentId,
                cardAmountUnitInclVat,
                balanceAmountUnitInclVat: unitPriceBalanceToPayInclVat,
                info
              })
          },
          {
            onSuccess: () => {
              handleCreateOrderSuccess({ orderId });
              setSearchParams({ orderId });
            }
          }
        );
        return;
      }

      const currentOrderId =
        isSplitPaymentActive && splitPaymentOrderId
          ? splitPaymentOrderId
          : orderId;

      createCardOrderV3(
        {
          orderId: currentOrderId,
          paymentMethod,
          basket: basketOrder,
          groupName: groupName ?? undefined,
          employeeUserId,
          employeeChildId: employeeChildId ?? null,
          employeeCardHex: employeeCardHex ?? null,
          paidWithSaldoUnit: unitPriceBalanceToPayInclVat,
          deviceInfo: { os, osVersion },
          unitCashReceived: unitPriceSplitCashInclVat,
          info,
          customShopperName: shopperName ?? undefined,
          sessionId: till.sessionId
        },
        {
          onSuccess: () => {
            handleCreateOrderSuccess({ orderId: currentOrderId });
          }
        }
      );
    } else {
      // Split payment flow - order already created
      if (isSplitPaymentActive) {
        return;
      }

      createOrder(
        {
          paymentMethod,
          basket: basketOrder,
          groupName: groupName ?? undefined,
          employeeUserId,
          employeeChildId: employeeChildId ?? null,
          employeeCardHex: employeeCardHex ?? null,
          paidWithSaldoUnit: unitPriceBalanceToPayInclVat,
          info,
          customShopperName: shopperName ?? undefined
        },
        {
          onSuccess: (order) => {
            setSearchParams({ orderId: order.id });

            // Reimbursement flow
            if (
              paymentMethod === "cash" &&
              unitPriceTotalInclVat < 0 &&
              os &&
              osVersion
            ) {
              payOrder({
                orderId: order.id,
                deviceInfo: { os, osVersion },
                paymentProvider: "cash",
                unitCashReceived: unitPriceTotalInclVat
              });
              openCashDrawer("automatic");
              createReceipt({ orderId: order.id, userId: employeeUserId });
            }

            let orderLines: Omit<CollectionEventPayload, "type">[] = [];
            let isFullyCollected = false;

            if (isCollectFlowActive) {
              orderLines = parseOrderLinesToCollect({
                orderLineToCollectIds: orderLinesToCollect,
                activeOrderLines: collectOrder.activeOrderLines
              });

              isFullyCollected =
                collectOrder.activeOrderLines.length ===
                orderLinesToCollect.length;

              collectOrderLines(
                {
                  childId: employeeChildId ?? "",
                  orderLines
                },
                {
                  onSuccess: () => {
                    collectActions.setSuccessInfo({
                      collectedOrderLines: orderLines.length,
                      additionalProductsPurchased: basketOrder.length,
                      isFullyCollected
                    });
                  }
                }
              );
            }

            if (paymentMethod === "credit" || paymentMethod === "billing") {
              if (
                till?.type === "self-service" &&
                till.config.receiptPrintMode === "automatic"
              ) {
                createReceipt({ orderId: order.id, userId: employeeUserId });
              }

              skipReceipt(order.id);
              if (isCollectFlowActive) {
                if (
                  hasAttendedSellConfig(till) &&
                  till.config.skipReceiptPage
                ) {
                  handleCollectSuccess({
                    collectedOrderLines: orderLines.length,
                    additionalProductsPurchased: basketOrder.length,
                    isFullyCollected,
                    forceRedirect: true
                  });
                }
              }
            }
          }
        }
      );
    }
  };

  const isSubmitDisabled =
    productsInBasket.length === 0 ||
    status === "pending" ||
    cardStatusV3 === "pending" ||
    tillStatus === "pending" ||
    cardReimbursementStatus === "pending" ||
    isSplitPaymentLoading ||
    isIdentifyUserPending;

  const unitAvailableInclVat =
    userProducts?.user?.collectLimit?.unitAvailableInclVat;

  const unitPriceBalanceAvailableInclVat = useUserBalance({
    unitAvailableInclVat,
    balance
  });

  const { payWithSaldo: unitPriceBalanceToPayInclVat } =
    calculateAmountForSaldo(
      unitPriceTotalInclVat,
      unitPriceBalanceAvailableInclVat
    );

  // Order and card payment error
  const hasCardError = cardStatusV3 === "error";

  // Order and card reimbursement error
  const hasCardReimbursementError = cardReimbursementStatus === "error";

  // Order and all other payment methods but card error
  const hasOrderError = status === "error";

  if (tillStatus !== "success") return null;

  const basketContainsSomeHybridBilling = productsInBasket.some(
    (product) => product.discount
  );

  const isInvoiceVisible =
    hasAttendedSellConfig(till) &&
    till.paymentProviders.includes("billing-selector") &&
    !(!isBasketEmpty && unitPriceTotalInclVat === 0) &&
    !basketContainsSomeHybridBilling;

  const isOrderDiscountVisible =
    hasAttendedSellConfig(till) &&
    !(!isBasketEmpty && unitPriceSubTotalInclVat <= 0) &&
    // TO-DO: remove when it will be possible to add a discount POS on top of a hybridBilling
    !basketContainsSomeHybridBilling;

  return (
    <div className="flex flex-col">
      {(hasAttendedSellConfig(till) || till.type === "self-service") &&
      till.config?.hasCustomNameInput &&
      (!employeeUserId || !employeeCardHex) ? (
        <Link className="w-full p-4" to={`${pathname}/shopper-name`}>
          <Button className="w-full" variant="secondary">
            {shopperName ? (
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faUser} />
                <p>{shopperName}</p>
              </div>
            ) : (
              <T _str="Add name" />
            )}
          </Button>
        </Link>
      ) : null}

      <Allowances allowances={allowances} currency={currency} locale={locale} />

      <DiningOptionSwitch />

      <div className="my-2 flex w-full justify-center">
        {isOrderDiscountVisible ? (
          <Link
            className={clsx({
              "pointer-events-none": isSubmitDisabled || isSplitPaymentActive
            })}
            to={`${pathname}/discount-order`}
          >
            <Button
              disabled={isSubmitDisabled || isSplitPaymentActive}
              icon={faPlus}
              variant="light"
            >
              <T _str="Discount" />
            </Button>
          </Link>
        ) : null}

        {isInvoiceVisible ? (
          <Link
            className={clsx({
              "pointer-events-none": isSubmitDisabled || isSplitPaymentActive
            })}
            to={`${pathname}/invoice`}
          >
            <Button
              disabled={isSubmitDisabled || isSplitPaymentActive}
              icon={faFileInvoice}
              variant="light"
            >
              <T _str="Invoice" />
            </Button>
          </Link>
        ) : null}
      </div>

      {till.moduleConfig.hidePrices ? null : (
        <PriceOverview
          unitPriceSubTotalInclVat={unitPriceSubTotalInclVat}
          unitPriceTotalInclVat={unitPriceTotalInclVat}
          unitPriceDiscountInclVat={unitPriceOrderDiscountInclVat}
          unitPriceSplitCashInclVat={unitPriceSplitCashInclVat}
          unitPriceBalanceToPayInclVat={unitPriceBalanceToPayInclVat}
          unitPriceTotalExclVat={unitPriceTotalExclVat}
          depositTotalInclVat={depositTotalInclVat}
          depositTotalExclVat={depositTotalExclVat}
          taxTotals={resolveBasketTaxTotals({
            showPricesInclVat: till.showPricesInclVat,
            // The fee has its own row; only its VAT belongs in the tax line.
            items: productsInBasket.map((product) => ({
              unitTotalPriceInclVat: product.totalPrice,
              unitTotalPriceExclVat: product.totalUnitPriceExclVat,
              vatRate: product.vatRate,
              depositTotalInclVat: product.depositTotal,
              depositTotalExclVat: product.depositTotalExclVat
            }))
          })}
          vatLabel={vatLabel}
          showPricesInclVat={till.showPricesInclVat}
          locale={locale}
          currency={currency}
          collectLimit={userProducts?.user?.collectLimit ?? null}
        />
      )}

      {isCollectFlowActive && isBasketEmpty ? (
        <CollectConfirmButton
          order={collectOrder}
          childId={employeeChildId ?? ""}
        />
      ) : (
        <PaymentButtons
          isSubmitDisabled={isSubmitDisabled}
          onCreate={handleCreateOrder}
          unitPriceBalance={balance}
          unitPriceCollectAvailableInclVat={unitAvailableInclVat ?? null}
          unitPriceBasketTotalInclVat={unitPriceTotalInclVat}
          unitPriceBalanceToPayInclVat={unitPriceBalanceToPayInclVat}
          isBasketEmpty={isBasketEmpty}
          isSplitPaymentActive={isSplitPaymentActive}
          isUserAuthenticated={isUserAuthenticated}
          till={till}
        />
      )}

      <OrderCreateErrorDialog
        error={[error, cardErrorV3, cardReimbursementError].find(Boolean)}
        open={hasOrderError || hasCardError || hasCardReimbursementError}
        reset={() => {
          if (hasOrderError) reset();
          if (hasCardError) {
            cardResetV3();
          }
          if (hasCardReimbursementError) cardReimbursementReset();
        }}
      />
    </div>
  );
};
